"use client"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import type { ComparisonPlanId, MetricId } from "./metricDetailsTypes"
import type { ExplainerModelSeries } from "@/data/plan-a/parsePlanAData"

// There is deliberately NO phase machine and NO timers here: both HUD layers
// (variant and details) stay mounted across open/close, so every motion is a
// CSS transition on a stable element (the sequential-fade classes on the
// layer divs in Playbook.tsx) and nothing can jump at a mount/unmount seam.
//
// Flight duration for the FLIP-flight components (FlyingMetricLabel /
// FlyingPanelElements). This branch no longer mounts them — the sequential
// fade replaced the flights — but the files (and this constant they import)
// are kept to minimize merge friction with main, which still uses them.
export const METRIC_DETAILS_FLY_MS = 480

// Small, focused context so opening/closing the details view does not churn
// every HudContext consumer (that value is memoised over ~15 per-frame deps).
// `modelSeries` carries the server-parsed per-plan series for the metrics whose
// chart is sourced live from the model (income, alignment); absent for the rest.
type MetricDetailsValue = {
  openId: MetricId | null
  // The clicked trigger element, kept so closing can hand keyboard focus
  // back to it (MetricDetailsHudState) — the variant layer never unmounts,
  // so the element survives while the details are open. Null when opened
  // without an element (deep link): focus stays where it is.
  originEl: HTMLElement | null
  openMetric: (id: MetricId, originEl?: HTMLElement | null) => void
  close: () => void
  modelSeries: ExplainerModelSeries
  // The non-A plan whose branch line the explainer's now-dot and readout ride
  // while the reader is on that plan's timeline (see usePlaybookState's
  // metricComparisonPlan for the why); null on Plan A's timeline.
  comparisonPlan: ComparisonPlanId | null
}

const MetricDetailsRef = createContext<MetricDetailsValue | null>(null)

// No-op fallback so triggers rendered outside the provider degrade to inert
// clicks rather than crashing (mirrors useHudContext's fallback).
const FALLBACK_METRIC_DETAILS: MetricDetailsValue = {
  openId: null,
  originEl: null,
  openMetric: () => {},
  close: () => {},
  modelSeries: {},
  comparisonPlan: null,
}

// Module-level so an omitted prop keeps a stable identity — a `= {}` default
// parameter would mint a new object per render and churn every consumer.
const EMPTY_MODEL_SERIES: ExplainerModelSeries = {}

type MetricDetailsState = {
  openId: MetricId | null
  originEl: HTMLElement | null
}

export const MetricDetailsProvider = ({
  children,
  modelSeries = EMPTY_MODEL_SERIES,
  comparisonPlan = null,
}: {
  children: ReactNode
  modelSeries?: ExplainerModelSeries
  comparisonPlan?: ComparisonPlanId | null
}) => {
  const [state, setState] = useState<MetricDetailsState>({
    openId: null,
    originEl: null,
  })

  const openMetric = useCallback((id: MetricId, originEl?: HTMLElement | null) => {
    setState({ openId: id, originEl: originEl ?? null })
  }, [])

  // Keep originEl on close: the label flies home to it, and it stays valid
  // because the variant layer (which contains it) is always mounted.
  const close = useCallback(() => {
    setState((prev) => (prev.openId ? { ...prev, openId: null } : prev))
  }, [])

  const value = useMemo<MetricDetailsValue>(
    () => ({ openId: state.openId, originEl: state.originEl, openMetric, close, modelSeries, comparisonPlan }),
    [state, openMetric, close, modelSeries, comparisonPlan]
  )
  return <MetricDetailsRef.Provider value={value}>{children}</MetricDetailsRef.Provider>
}

export const useMetricDetails = (): MetricDetailsValue =>
  useContext(MetricDetailsRef) ?? FALLBACK_METRIC_DETAILS
